import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Step,
  Stepper,
  StepLabel,
  ThemeProvider,
  createTheme,
  Checkbox,
  Link,
  Tooltip} from '@mui/material';
import { InfoCircleOutlined } from '@ant-design/icons';
import { styled } from '@mui/system';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SendIcon from '@mui/icons-material/Send';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DownloadIcon from '@mui/icons-material/Download';
import SectionTitle from './text_components/SectionTitle';
import { Select } from 'antd';
import { useMediaQuery } from 'react-responsive';
import { fetchClientNames, sendSurveyInvitations } from '../api/participantcensusApi';
import { fetchAdvisorNames } from '../api/participantcensusApi';
import { message } from 'antd';

const { Option } = Select;

const theme = createTheme({
  palette: {
    primary: {
      main: '#4a90e2',
    },
    secondary: {
      main: '#f50057',
    },
    success: {
      main: '#52c41a',
    },
  },
});

const StyledCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  transition: all 0.3s ease;
  border: 1px solid #bfdbfe;

  &:hover {
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
    transform: translateY(-5px);
  }
`;

const StyledSelect = styled(Select)`
  .ant-select-selector {
    border-radius: 10px !important;
    border: 2px solid #e6e6e6 !important;
    transition: all 0.3s ease;
  }

  &:hover .ant-select-selector,
  &.ant-select-focused .ant-select-selector {
    border-color: #4a90e2 !important;
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2) !important;
  }
`;

const StyledButton = styled(Button)`
  border-radius: 10px;
  height: 40px;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const StyledStepper = styled(Stepper)(({ theme }) => ({
  '& .MuiStepIcon-root': {
    color: theme.palette.grey[300],
    '&.Mui-active': {
      color: theme.palette.primary.main,
    },
    '&.Mui-completed': {
      color: theme.palette.success.main,
    },
  },
}));

const StyledInputWrapper = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: '8px',
  boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.23)',
  transition: 'box-shadow 0.3s ease',
  '&:hover': {
    boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.87)',
  },
  '&:focus-within': {
    boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
  },
}));

const StyledInput = styled('input')(({ theme }) => ({
  width: '100%',
  padding: '10px 12px',
  fontSize: '16px',
  border: 'none',
  borderRadius: '8px',
  outline: 'none',
  backgroundColor: 'transparent',
}));

// Add this new styled component near the top of the file, after other styled components
const CenteredContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh', // This ensures the container takes up the full viewport height
});

const MobileContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  minHeight: '100vh',
  padding: '24px',
  backgroundColor: '#f0f2f5',
});

const MobileCard = styled(motion.div)({
  background: 'white',
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  padding: '24px',
  marginBottom: '16px',
});

const MobileTitle = styled(Typography)({
  fontSize: '28px',
  fontWeight: 700,
  color: '#1a202c',
  marginBottom: '24px',
  textAlign: 'center',
});

const MobileInputGroup = styled('div')({
  marginBottom: '24px',
});

const MobileLabel = styled(Typography)({
  display: 'block',
  marginBottom: '8px',
  fontWeight: 600,
  color: '#4a5568',
});

const MobileButton = styled(Button)({
  width: '100%',
  height: '48px',
  fontSize: '16px',
  fontWeight: 600,
  borderRadius: '12px',
  textTransform: 'none',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
});

const MobileTable = styled(TableContainer)({
  maxHeight: '300px',
  overflowY: 'auto',
  marginTop: '16px',
  borderRadius: '12px',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
  '& .MuiTableCell-root': {
    padding: '12px 16px',
  },
});

// Add this styled component near other styled components
const renderLabel = (label, tooltip) => (
  <div className="flex items-center">
    <span>{label}</span>
    <Tooltip title={tooltip}>
      <InfoCircleOutlined className="text-gray-400 hover:text-gray-600 ml-2" />
    </Tooltip>
  </div>
);

// Add this styled component near the other styled components at the top
const StyledFormSection = styled(motion.div)`
  background: white;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  transition: all 0.3s ease;
  border: 1px solid #bfdbfe;
  margin-bottom: 2rem;

  &:hover {
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
    transform: translateY(-5px);
  }
`;

function ParticipantCensus() {
  const [participants, setParticipants] = useState([]);
  const [companyName, setCompanyName] = useState('');
  const [manualName, setManualName] = useState('');
  const [manualEmail, setManualEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [companyError, setCompanyError] = useState('');
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [advisorName, setAdvisorName] = useState('');
  const [advisorNames, setAdvisorNames] = useState([]);
  useEffect(() => {
    const loadAdvisorNames = async () => {
      try {
        const names = await fetchAdvisorNames();
        setAdvisorNames(names.filter(name => name)); // Filter out empty strings
      } catch (err) {
        console.error('Error fetching advisor names:', err);
      }
    };
    loadAdvisorNames();
  }, []);

  // Update the useEffect that fetches companies
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setLoading(true);
        const clientNames = await fetchClientNames();
        setCompanies(clientNames);
      } catch (err) {
        console.error('Error fetching companies:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanies();
  }, []); // Empty dependency array means this runs once when component mounts

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
  };

  const isMobile = useMediaQuery({ maxWidth: 767 });

  const renderMobileContent = () => {
    return (
      <MobileContainer>
        <MobileCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <MobileTitle variant="h4">Participant Census</MobileTitle>
          
          <MobileInputGroup>
            {renderLabel(
              "Select Company",
              "Select the company for which you're managing participants"
            )}
            <StyledSelect
              style={{ width: '100%' }}
              value={companyName}
              onChange={(value) => {
                setCompanyName(value);
                setCompanyError('');
              }}
              className="text-lg"
              loading={loading}
            >
              {companies.map((company) => (
                <Option key={company} value={company}>{company}</Option>
              ))}
            </StyledSelect>
            {companyError && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {companyError}
              </Typography>
            )}
          </MobileInputGroup>

          <MobileInputGroup>
            <MobileButton
              component="label"
              startIcon={<CloudUploadIcon />}
              variant="contained"
              color="primary"
              fullWidth
            >
              Upload Excel File
              <VisuallyHiddenInput type="file" accept=".xlsx, .xls" onChange={handleFileUpload} />
            </MobileButton>
          </MobileInputGroup>

          <MobileInputGroup>
            <Link 
              href="#" 
              onClick={handleTemplateDownload}
              style={{ textDecoration: 'none' }}
            >
              <MobileButton
                startIcon={<DownloadIcon />}
                variant="outlined"
                color="primary"
                fullWidth
              >
                Download Template
              </MobileButton>
            </Link>
          </MobileInputGroup>

          <MobileInputGroup>
            {renderLabel(
              "Add Participant Manually",
              "Enter participant details to add them one at a time"
            )}
            <TextField
              fullWidth
              placeholder="Name"
              value={manualName}
              onChange={(e) => setManualName(e.target.value)}
              sx={{ mb: 1 }}
            />
            <TextField
              fullWidth
              placeholder="Email"
              type="email"
              value={manualEmail}
              onChange={(e) => setManualEmail(e.target.value)}
              sx={{ mb: 1 }}
            />
            <MobileButton
              onClick={addManualParticipant}
              startIcon={<PersonAddIcon />}
              variant="contained"
              fullWidth
            >
              Add Participant
            </MobileButton>
          </MobileInputGroup>

          {participants.length > 0 && (
            <MobileInputGroup>
              <MobileLabel>Added Participants</MobileLabel>
              <MobileTable>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {participants.map((participant, index) => (
                      <TableRow key={index}>
                        <TableCell>{participant.name}</TableCell>
                        <TableCell>{participant.email}</TableCell>
                        <TableCell>
                          <IconButton onClick={() => removeParticipant(index)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </MobileTable>
            </MobileInputGroup>
          )}

          <MobileButton
            variant="contained"
            color="primary"
            onClick={sendSurveys}
            disabled={sending || participants.length === 0}
            startIcon={<SendIcon />}
          >
            {sending ? 'Sending...' : 'Send Surveys'}
          </MobileButton>
        </MobileCard>
      </MobileContainer>
    );
  };

  // Handle Excel file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (evt) => {
        const data = evt.target.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const firstSheet = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheet];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        parseExcelData(jsonData);
      };
      reader.onerror = () => {
        message.error('Error reading file');
      };
      reader.readAsBinaryString(file);
    }
  };

  // Parse Excel data into participants list
  const parseExcelData = (data) => {
    if (data.length > 1) {
      const headers = data[0];
      const nameIndex = headers.findIndex((h) => 
        h.toLowerCase() === 'participant name' || h.toLowerCase() === 'participantname'
      );
      const emailIndex = headers.findIndex((h) => 
        h.toLowerCase() === 'email address' || h.toLowerCase() === 'emailaddress'
      );
      
      if (nameIndex === -1 || emailIndex === -1) {
        message.error('Excel file must have "Participant Name" and "Email Address" columns');
        return;
      }
      
      const parsedParticipants = data.slice(1).map((row) => ({
        name: row[nameIndex],
        email: row[emailIndex],
      }));
      setParticipants(parsedParticipants);
      message.success('Participants loaded successfully');
    } else {
      message.error('Excel file is empty or improperly formatted');
    }
  };

  // Add participant manually
  const addManualParticipant = () => {
    if (manualName && manualEmail) {
      setParticipants([...participants, { name: manualName, email: manualEmail }]);
      setManualName('');
      setManualEmail('');
      message.success('Participant added successfully');
    } else {
      message.error('Please enter both name and email');
    }
  };

  // Remove a participant from the list
  const removeParticipant = (index) => {
    const updatedParticipants = [...participants];
    updatedParticipants.splice(index, 1);
    setParticipants(updatedParticipants);
  };

  // Send surveys to participants
  const sendSurveys = async () => {
    if (!companyName) {
      message.error('Please select a company');
      return;
    }
    if (!advisorName) {
      message.error('Please select an advisor');
      return;
    }
    if (participants.length === 0) {
      message.error('Please add at least one participant before sending surveys');
      return;
    }

    setSending(true);

    try {
      const result = await sendSurveyInvitations(
        participants,
        companyName,
        advisorName
      );

      message.success(`Surveys sent successfully to ${result.participants_count} participants`);
      setParticipants([]);
      setCurrentStep(0);
    } catch (error) {
      console.error('Error sending surveys:', error);
      message.error(`Failed to send surveys: ${error.message}`);
    } finally {
      setSending(false);
    }
  };

  const steps = ['Company Info', 'Add Participants', 'Review & Send'];

  const handleNext = () => {
    if (currentStep === 0 && !companyName) {
      setCompanyError('Please select a company before proceeding');
      return;
    }
    setCompanyError(''); // Clear the error when moving to the next step
    setCurrentStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setCurrentStep((prevStep) => prevStep - 1);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedParticipants(participants.map((_, index) => index));
    } else {
      setSelectedParticipants([]);
    }
  };

  const handleSelectParticipant = (index) => {
    setSelectedParticipants(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const deleteSelectedParticipants = () => {
    const updatedParticipants = participants.filter((_, index) => !selectedParticipants.includes(index));
    setParticipants(updatedParticipants);
    setSelectedParticipants([]);
  };

  const resetParticipants = () => {
    setParticipants([]);
    setSelectedParticipants([]);
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <motion.div initial="hidden" animate="visible" variants={fadeIn}>
            <StyledFormSection>
              <Typography variant="h6" gutterBottom>Presentation General Information</Typography>
              
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, mb: 1 }}>
                {renderLabel(
                  "Client Name",
                  "Select the company for which you're managing participants"
                )}
              </Typography>
              <StyledSelect
                style={{ width: '100%', marginBottom: '4px' }}
                value={companyName}
                onChange={(value) => {
                  setCompanyName(value);
                  setCompanyError('');
                }}
                className="text-lg"
                loading={loading}
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {companies.map((company) => (
                  <Option key={company} value={company}>{company}</Option>
                ))}
              </StyledSelect>
              
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, mb: 1 }}>
                {renderLabel(
                  "Select Advisor",
                  "Choose the advisor responsible for this client"
                )}
              </Typography>
              <StyledSelect
                style={{ width: '100%', marginBottom: '4px' }}
                value={advisorName}
                onChange={(value) => setAdvisorName(value)}
                placeholder="Select Advisor"
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {advisorNames.map((advisor) => (
                  <Option key={advisor} value={advisor}>{advisor}</Option>
                ))}
              </StyledSelect>
              
              {companyError && (
                <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                  {companyError}
                </Typography>
              )}
            </StyledFormSection>
          </motion.div>
        );
      case 1:
      case 2:
        return (
          <motion.div initial="hidden" animate="visible" variants={fadeIn}>
            <Typography variant="h6" gutterBottom>{step === 1 ? 'Add Participants' : 'Review Participants'}</Typography>
            {step === 1 && (
              <>
                <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <StyledButton
                    component="label"
                    startIcon={<CloudUploadIcon />}
                    variant="contained"
                    color="primary"
                  >
                    Upload Excel File
                    <VisuallyHiddenInput type="file" accept=".xlsx, .xls" onChange={handleFileUpload} />
                  </StyledButton>
                  <Link 
                    href="#" 
                    onClick={handleTemplateDownload}
                    style={{ textDecoration: 'none' }}
                  >
                    <StyledButton
                      startIcon={<DownloadIcon />}
                      variant="outlined"
                      color="primary"
                    >
                      Download Template
                    </StyledButton>
                  </Link>
                </Box>
                <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
                  Please use the template file for uploading participants. The file must contain 'Participant Name' and 'Email Address' columns.
                </Typography>
                <StyledCard sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>Or Add Manually</Typography>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-end' }}>
                    <div style={{ flex: 1 }}>
                      {renderLabel(
                        "Participant Name",
                        "Enter the participant's full name"
                      )}
                      <StyledInputWrapper>
                        <StyledInput
                          placeholder="Name"
                          value={manualName}
                          onChange={(e) => setManualName(e.target.value)}
                        />
                      </StyledInputWrapper>
                    </div>
                    
                    <div style={{ flex: 1 }}>
                      {renderLabel(
                        "Email Address",
                        "Enter a valid email address for the participant"
                      )}
                      <StyledInputWrapper>
                        <StyledInput
                          placeholder="Email"
                          type="email"
                          value={manualEmail}
                          onChange={(e) => setManualEmail(e.target.value)}
                        />
                      </StyledInputWrapper>
                    </div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                      <StyledButton
                        onClick={addManualParticipant}
                        startIcon={<PersonAddIcon />}
                        variant="contained"
                      >
                        Add
                      </StyledButton>
                    </div>
                  </Box>
                </StyledCard>
              </>
            )}
            
            <StyledCard sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>Current Participants</Typography>
              {participants.length > 0 ? (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <StyledButton
                      onClick={deleteSelectedParticipants}
                      startIcon={<DeleteIcon />}
                      variant="contained"
                      color="error"
                      disabled={selectedParticipants.length === 0}
                    >
                      Delete Selected
                    </StyledButton>
                    <StyledButton
                      onClick={resetParticipants}
                      variant="outlined"
                      color="secondary"
                    >
                      Reset All
                    </StyledButton>
                  </Box>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              indeterminate={selectedParticipants.length > 0 && selectedParticipants.length < participants.length}
                              checked={selectedParticipants.length === participants.length}
                              onChange={handleSelectAll}
                            />
                          </TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell>Email</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {participants.map((participant, index) => (
                          <TableRow 
                            key={index}
                            selected={selectedParticipants.includes(index)}
                            onClick={() => handleSelectParticipant(index)}
                            hover
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedParticipants.includes(index)}
                                onChange={() => handleSelectParticipant(index)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </TableCell>
                            <TableCell>{participant.name}</TableCell>
                            <TableCell>{participant.email}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : (
                <Typography variant="body1" color="error">No participants added yet. Please add at least one participant.</Typography>
              )}
            </StyledCard>
          </motion.div>
        );
      default:
        return 'Unknown step';
    }
  };

  const handleTemplateDownload = async (e) => {
    console.log('Attempting download...');
    e.preventDefault();
    
    // Try multiple path variations to debug
    const paths = [
      '/Participants-Census-401k-Pro.xlsx',
      './Participants-Census-401k-Pro.xlsx',
      'Participants-Census-401k-Pro.xlsx',
      `${process.env.PUBLIC_URL}/Participants-Census-401k-Pro.xlsx`
    ];

    for (const path of paths) {
      console.log(`Trying path: ${path}`);
      try {
        const response = await fetch(path);
        console.log(`Response for ${path}:`, response.status);
        
        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'Participants-Census-401k-Pro.xlsx';
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          console.log('Download successful with path:', path);
          return;
        }
      } catch (error) {
        console.error(`Error with path ${path}:`, error);
      }
    }
    
    // If we get here, none of the paths worked
    message.error('Template file not found. Please contact support.');
    console.error('All download attempts failed');
  };

  return (
    <ThemeProvider theme={theme}>
      {isMobile ? (
        renderMobileContent()
      ) : (
        <CenteredContainer>
          <Container maxWidth="md">
            <StyledCard sx={{ mt: 4, mb: 4 }}>
              <motion.div initial="hidden" animate="visible" variants={fadeIn}>
                <SectionTitle>Participant Census</SectionTitle>
              </motion.div>
              <StyledStepper activeStep={currentStep} alternativeLabel>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </StyledStepper>

              <Box sx={{ mt: 4 }}>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentStep}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    {getStepContent(currentStep)}
                  </motion.div>
                </AnimatePresence>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                <StyledButton
                  disabled={currentStep === 0}
                  onClick={handleBack}
                >
                  Previous
                </StyledButton>
                <StyledButton
                  variant="contained"
                  color="primary"
                  onClick={currentStep === steps.length - 1 ? sendSurveys : handleNext}
                  disabled={sending || (currentStep === 1 && participants.length === 0)}
                  startIcon={currentStep === steps.length - 1 ? <SendIcon /> : null}
                >
                  {currentStep === steps.length - 1 ? (sending ? 'Sending...' : 'Send Surveys') : 'Next'}
                </StyledButton>
              </Box>
            </StyledCard>

            {/* <div style={{ margin: '20px 0' }}>
              <Typography variant="body2" color="error">
                Debug Download Links:
              </Typography>
              <div>
                <a href="/Participants-Census-401k-Pro.xlsx" download>Direct Link 1</a>
              </div>
              <div>
                <a href={`${process.env.PUBLIC_URL}/Participants-Census-401k-Pro.xlsx`} download>Direct Link 2</a>
              </div>
            </div> */}
          </Container>
        </CenteredContainer>
      )}
    </ThemeProvider>
  );
}

export default ParticipantCensus;
